import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Pending as PendingIcon,
  Person,
  PersonOutline,
  Phone,
  LocationOn,
  Home,
  ContactPhone,
  Description,
  DirectionsCar,
  LocalShipping,
  Event,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useAuth } from "../context/auth";
import { usePermissions } from "../context/PermissionContext";
import PermissionGuard from "./PermissionGuard";
import ConfirmDialog from "./ConfirmDialog";

const ApprovalManagement = () => {
  const [approvals, setApprovals] = useState([]);
  const [filteredApprovals, setFilteredApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [approveDialog, setApproveDialog] = useState({
    open: false,
    approvalId: null,
    customId: ''
  });
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [currentTab, setCurrentTab] = useState(0); // 0: Pending, 1: All

  const { token, isAuthenticated } = useAuth();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_BASE_URL;

  // Vehicle type mapping
  const vehicleTypeMap = {
    1: "Motorcycle",
    2: "Tricycle",
  };

  const statusColors = {
    pending: "warning",
    approved: "success",
    rejected: "error",
  };

  useEffect(() => {
    fetchApprovals();
  }, [currentTab]);

  useEffect(() => {
    filterApprovals();
  }, [approvals, currentTab]);

  const fetchApprovals = async () => {
    try {
      setLoading(true);
      setError("");

      if (!isAuthenticated || !token) {
        setError("Please login to view approvals");
        setLoading(false);
        return;
      }

      const endpoint = currentTab === 0
        ? `${baseUrl}/api/approvals/pending`
        : `${baseUrl}/api/approvals`;

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setApprovals(response.data);
    } catch (err) {
      console.error("Error fetching approvals:", err);
      setError(err.response?.data?.error || "Failed to fetch approvals");
    } finally {
      setLoading(false);
    }
  };

  const filterApprovals = () => {
    if (currentTab === 0) {
      setFilteredApprovals(approvals.filter(a => a.status === 'pending'));
    } else {
      setFilteredApprovals(approvals);
    }
  };

  const handleViewDetails = (approval) => {
    navigate(`/dashboard/approvals/${approval.id}`);
  };

  const handleApproveClick = (approval) => {
    if (!hasPermission('approval', 'approve')) {
      setError('You do not have permission to approve bookmarks');
      return;
    }

    setApproveDialog({
      open: true,
      approvalId: approval.id,
      customId: approval.customId
    });
  };

  const handleCloseApproveDialog = () => {
    if (!actionLoading) {
      setApproveDialog({
        open: false,
        approvalId: null,
        customId: ''
      });
    }
  };

  const handleApproveConfirm = async () => {
    try {
      setActionLoading(true);
      setError("");
      setSuccess("");

      await axios.post(
        `${baseUrl}/api/approvals/${approveDialog.approvalId}/approve`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSuccess("Bookmark approved successfully!");
      fetchApprovals();
      setDetailsDialog(false);
      setApproveDialog({
        open: false,
        approvalId: null,
        customId: ''
      });
    } catch (err) {
      console.error("Error approving bookmark:", err);
      setError(err.response?.data?.error || "Failed to approve bookmark");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectClick = (approval) => {
    setSelectedApproval(approval);
    setRejectionReason("");
    setRejectDialog(true);
  };

  const handleRejectConfirm = async () => {
    if (!hasPermission('approval', 'reject')) {
      setError('You do not have permission to reject bookmarks');
      return;
    }

    if (!rejectionReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    try {
      setActionLoading(true);
      setError("");
      setSuccess("");

      await axios.post(
        `${baseUrl}/api/approvals/${selectedApproval.id}/reject`,
        { rejectionReason },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSuccess("Bookmark rejected successfully!");
      fetchApprovals();
      setRejectDialog(false);
      setDetailsDialog(false);
    } catch (err) {
      console.error("Error rejecting bookmark:", err);
      setError(err.response?.data?.error || "Failed to reject bookmark");
    } finally {
      setActionLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <PermissionGuard resource="approval" action="view">
      <Container>
        <Box my={4}>
          <Typography variant="h4" gutterBottom>
            Bookmark Approval Workflow
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Review and approve or reject pending bookmark registrations
          </Typography>

          {error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" onClose={() => setSuccess("")} sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3 }}>
            <Tabs value={currentTab} onChange={handleTabChange}>
              <Tab
                label={`Pending (${approvals.filter(a => a.status === 'pending').length})`}
                icon={<PendingIcon />}
                iconPosition="start"
              />
              <Tab label="All Approvals" />
            </Tabs>
          </Box>

          <TableContainer component={Paper} sx={{ mt: 3, overflowX: 'auto' }}>
            <Table sx={{ minWidth: { xs: 650, md: 'auto' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Custom ID</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Vehicle Type</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Owner Name</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Contact</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Status</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Submitted Date</TableCell>
                  <TableCell align="center" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredApprovals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="textSecondary">
                        {currentTab === 0 ? 'No pending approvals' : 'No approvals found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredApprovals.map((approval) => (
                    <TableRow key={approval.id}>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{approval.customId}</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{vehicleTypeMap[approval.vehicleType] || 'Unknown'}</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {`${approval.ofirstName || ''} ${approval.omiddleName || ''} ${approval.olastName || ''}`}
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{approval.ocontact}</TableCell>
                      <TableCell>
                        <Chip
                          label={approval.status.toUpperCase()}
                          color={statusColors[approval.status]}
                          size="small"
                          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {new Date(approval.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          onClick={() => handleViewDetails(approval)}
                          title="View Details"
                          
                        >
                          <ViewIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                        </IconButton>
                        {approval.status === 'pending' && (
                          <>
                            {hasPermission('approval', 'approve') && (
                              <IconButton
                                color="success"
                                onClick={() => handleApproveClick(approval)}
                                disabled={actionLoading}
                                title="Approve"
                                
                              >
                                <ApproveIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                              </IconButton>
                            )}
                            {hasPermission('approval', 'reject') && (
                              <IconButton
                                color="error"
                                onClick={() => handleRejectClick(approval)}
                                disabled={actionLoading}
                                title="Reject"
                                
                              >
                                <RejectIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                              </IconButton>
                            )}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Details Dialog */}
        <Dialog
          open={detailsDialog}
          onClose={() => setDetailsDialog(false)}
          maxWidth="lg"
          fullWidth
          fullScreen={window.innerWidth < 600}
          PaperProps={{
            sx: {
              minHeight: { xs: '100vh', sm: '80vh' },
              m: { xs: 0, sm: 2 }
            }
          }}
        >
          {selectedApproval && (
            <>
              <DialogTitle sx={{
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: { xs: 2, sm: 3 },
                flexWrap: 'wrap',
                gap: 1
              }}>
                <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }} flexWrap="wrap">
                  <Box>
                    <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                      {selectedApproval.customId}
                    </Typography>
                    <Chip
                      label={selectedApproval.status.toUpperCase()}
                      color={statusColors[selectedApproval.status]}
                      size="small"
                      sx={{ mt: 1, fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
                    />
                  </Box>
                </Box>
                <IconButton onClick={() => setDetailsDialog(false)} sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ pt: { xs: 2, sm: 3 }, px: { xs: 1, sm: 2, md: 3 } }}>
                {/* Quick Info Cards */}
                <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: 'linear-gradient(135deg, #2196F3 0%, #64B5F6 100%)', color: 'white' }}>
                      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Vehicle Type</Typography>
                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                              {vehicleTypeMap[selectedApproval.vehicleType] || 'Unknown'}
                            </Typography>
                          </Box>
                          {selectedApproval.vehicleType === 1 ?
                            <DirectionsCar sx={{ fontSize: { xs: 32, sm: 40 }, opacity: 0.7 }} /> :
                            <LocalShipping sx={{ fontSize: { xs: 32, sm: 40 }, opacity: 0.7 }} />
                          }
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)', color: 'white' }}>
                      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Owner</Typography>
                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {`${selectedApproval.ofirstName || ''} ${selectedApproval.olastName || ''}`}
                            </Typography>
                          </Box>
                          <Person sx={{ fontSize: { xs: 32, sm: 40 }, opacity: 0.7, ml: 1 }} />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)', color: 'white' }}>
                      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Operator</Typography>
                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {selectedApproval.opfirstName ?
                                `${selectedApproval.opfirstName} ${selectedApproval.oplastName}` :
                                'Not Assigned'}
                            </Typography>
                          </Box>
                          <PersonOutline sx={{ fontSize: { xs: 32, sm: 40 }, opacity: 0.7, ml: 1 }} />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)', color: 'white' }}>
                      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Submitted</Typography>
                            <Typography variant="body1" fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                              {new Date(selectedApproval.createdAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                          <Event sx={{ fontSize: { xs: 32, sm: 40 }, opacity: 0.7 }} />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Owner and Operator Information Row */}
                <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} sx={{ mb: { xs: 1.5, sm: 2 } }}>
                  {/* Owner Information */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                        <Typography variant="h6" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                          <Person sx={{ mr: 1 }} />
                          Owner Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={4} sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                            {selectedApproval.ownerProfileImage ? (
                              <Box
                                component="img"
                                src={`${baseUrl}/${selectedApproval.ownerProfileImage}`}
                                alt="Owner Profile"
                                sx={{
                                  width: 120,
                                  height: 120,
                                  mx: 'auto',
                                  boxShadow: 3,
                                  border: '4px solid #f0f0f0',
                                  borderRadius: 2,
                                  objectFit: 'cover'
                                }}
                              />
                            ) : (
                              <Box sx={{
                                width: 120,
                                height: 120,
                                mx: 'auto',
                                bgcolor: '#4CAF50',
                                fontSize: '2.5rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 2,
                                color: 'white',
                                fontWeight: 'bold'
                              }}>
                                {selectedApproval.ofirstName?.[0] || 'O'}
                              </Box>
                            )}
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <List dense>
                              <ListItem>
                                <ListItemIcon><Person color="primary" /></ListItemIcon>
                                <ListItemText
                                  primary="Full Name"
                                  secondary={`${selectedApproval.ofirstName || ''} ${selectedApproval.omiddleName || ''} ${selectedApproval.olastName || ''}`}
                                />
                              </ListItem>
                              <ListItem>
                                <ListItemIcon><Phone color="primary" /></ListItemIcon>
                                <ListItemText primary="Contact" secondary={selectedApproval.ocontact || 'Not provided'} />
                              </ListItem>
                              <ListItem>
                                <ListItemIcon><ContactPhone color="primary" /></ListItemIcon>
                                <ListItemText primary="Emergency Contact" secondary={selectedApproval.oemgContact || 'Not provided'} />
                              </ListItem>
                              <ListItem>
                                <ListItemIcon><LocationOn color="primary" /></ListItemIcon>
                                <ListItemText primary="District" secondary={selectedApproval.odistrict || 'Not provided'} />
                              </ListItem>
                              <ListItem>
                                <ListItemIcon><Home color="primary" /></ListItemIcon>
                                <ListItemText primary="Address" secondary={selectedApproval.oaddress || 'Not provided'} />
                              </ListItem>
                            </List>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Operator Information */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                        <Typography variant="h6" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonOutline sx={{ mr: 1 }} />
                          Operator Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={4} sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                            {selectedApproval.operatorProfileImage ? (
                              <Box
                                component="img"
                                src={`${baseUrl}/${selectedApproval.operatorProfileImage}`}
                                alt="Operator Profile"
                                sx={{
                                  width: 120,
                                  height: 120,
                                  mx: 'auto',
                                  boxShadow: 3,
                                  border: '4px solid #f0f0f0',
                                  borderRadius: 2,
                                  objectFit: 'cover'
                                }}
                              />
                            ) : (
                              <Box sx={{
                                width: 120,
                                height: 120,
                                mx: 'auto',
                                bgcolor: '#FF9800',
                                fontSize: '2.5rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 2,
                                color: 'white',
                                fontWeight: 'bold'
                              }}>
                                {selectedApproval.opfirstName?.[0] || 'N'}
                              </Box>
                            )}
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <List dense>
                              <ListItem>
                                <ListItemIcon><Person color="primary" /></ListItemIcon>
                                <ListItemText
                                  primary="Full Name"
                                  secondary={selectedApproval.opfirstName ?
                                    `${selectedApproval.opfirstName || ''} ${selectedApproval.opmiddleName || ''} ${selectedApproval.oplastName || ''}` :
                                    'Not Assigned'}
                                />
                              </ListItem>
                              <ListItem>
                                <ListItemIcon><Phone color="primary" /></ListItemIcon>
                                <ListItemText primary="Contact" secondary={selectedApproval.opContact || 'Not provided'} />
                              </ListItem>
                              <ListItem>
                                <ListItemIcon><ContactPhone color="primary" /></ListItemIcon>
                                <ListItemText primary="Emergency Contact" secondary={selectedApproval.opemgContact || 'Not provided'} />
                              </ListItem>
                              <ListItem>
                                <ListItemIcon><Description color="primary" /></ListItemIcon>
                                <ListItemText primary="Driver's License" secondary={selectedApproval.opdriverLicense || 'Not provided'} />
                              </ListItem>
                              <ListItem>
                                <ListItemIcon><Home color="primary" /></ListItemIcon>
                                <ListItemText primary="Address" secondary={selectedApproval.opAddress || 'Not provided'} />
                              </ListItem>
                            </List>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Vehicle Specifications */}
                <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} sx={{ mb: { xs: 1.5, sm: 2 } }}>
                  <Grid item xs={12}>
                    <Card>
                      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                        <Typography variant="h6" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                          <DirectionsCar sx={{ mr: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                          Vehicle Specifications
                        </Typography>
                        <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
                        <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">Business Name:</Typography>
                              <Typography variant="body1" fontWeight="500">{selectedApproval.vbusinessName || 'N/A'}</Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">VIN Number:</Typography>
                              <Chip label={selectedApproval.vvinNo || 'N/A'} color="primary" variant="outlined" size="small" />
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">Engine Number:</Typography>
                              <Chip label={selectedApproval.vengineNo || 'N/A'} color="primary" variant="outlined" size="small" />
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">K Number:</Typography>
                              <Typography variant="body1" fontWeight="500">{selectedApproval.vkNo || 'N/A'}</Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">Vehicle ID:</Typography>
                              <Typography variant="body1" fontWeight="500">{selectedApproval.vvehicleID || 'N/A'}</Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">Model:</Typography>
                              <Typography variant="body1" fontWeight="500">{selectedApproval.vmodel || 'N/A'}</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">Receipt Number:</Typography>
                              <Typography variant="body1" fontWeight="500">{selectedApproval.vreceiptNo || 'N/A'}</Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">License Plate:</Typography>
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  backgroundColor: 'white',
                                  color: 'black',
                                  padding: '4px 12px',
                                  borderRadius: 1,
                                  border: '2px solid #333',
                                  fontFamily: 'monospace',
                                  fontSize: '1rem',
                                  fontWeight: 'bold',
                                  letterSpacing: '0.05em'
                                }}
                              >
                                {selectedApproval.vlicensePlate || 'N/A'}
                              </Box>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">Color:</Typography>
                              <Typography variant="body1" fontWeight="500">{selectedApproval.vcolor || 'N/A'}</Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">Seller Address:</Typography>
                              <Typography variant="body1" fontWeight="500">{selectedApproval.vAddress || 'N/A'}</Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">Seller Contact:</Typography>
                              <Typography variant="body1" fontWeight="500">{selectedApproval.vsellerContact || 'N/A'}</Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">Purchase Date:</Typography>
                              <Typography variant="body1" fontWeight="500">
                                {selectedApproval.vpurchasedDate ?
                                  new Date(selectedApproval.vpurchasedDate).toLocaleDateString() :
                                  'N/A'}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Approval Status */}
                {selectedApproval.status !== 'pending' && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Alert severity={selectedApproval.status === 'approved' ? 'success' : 'error'}>
                        <Typography variant="h6" gutterBottom>
                          {selectedApproval.status === 'approved' ? 'Approved' : 'Rejected'}
                        </Typography>
                        {selectedApproval.status === 'approved' && (
                          <>
                            <Typography variant="body2">
                              <strong>Approved by:</strong> {selectedApproval.approver?.firstname} {selectedApproval.approver?.lastname}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Approved at:</strong> {new Date(selectedApproval.approvedAt).toLocaleString()}
                            </Typography>
                          </>
                        )}
                        {selectedApproval.status === 'rejected' && (
                          <>
                            <Typography variant="body2">
                              <strong>Rejected by:</strong> {selectedApproval.rejecter?.firstname} {selectedApproval.rejecter?.lastname}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Rejected at:</strong> {new Date(selectedApproval.rejectedAt).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              <strong>Reason:</strong> {selectedApproval.rejectionReason}
                            </Typography>
                          </>
                        )}
                      </Alert>
                    </Grid>
                  </Grid>
                )}
              </DialogContent>
              <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, borderTop: '1px solid #e0e0e0', gap: 1, flexWrap: 'wrap' }}>
                {selectedApproval.status === 'pending' && (
                  <>
                    {hasPermission('approval', 'approve') && (
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => handleApproveClick(selectedApproval)}
                        disabled={actionLoading}
                        startIcon={<ApproveIcon />}
                        
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        Approve
                      </Button>
                    )}
                    {hasPermission('approval', 'reject') && (
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleRejectClick(selectedApproval)}
                        disabled={actionLoading}
                        startIcon={<RejectIcon />}
                        
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        Reject
                      </Button>
                    )}
                  </>
                )}
                <Button onClick={() => setDetailsDialog(false)} variant="outlined"  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Approve Confirmation Dialog */}
        <ConfirmDialog
          open={approveDialog.open}
          onClose={handleCloseApproveDialog}
          onConfirm={handleApproveConfirm}
          title="Approve Bookmark"
          message={`Are you sure you want to approve bookmark registration "${approveDialog.customId}"?`}
          warning="Once approved, this bookmark will be moved to the active bookmarks list and will be visible to all users."
          severity="info"
          loading={actionLoading}
          confirmText="Approve Bookmark"
          confirmColor="success"
        />

        {/* Reject Dialog */}
        <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)}>
          <DialogTitle>Reject Bookmark Registration</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>
              Please provide a reason for rejecting this bookmark registration:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleRejectConfirm}
              disabled={actionLoading || !rejectionReason.trim()}
            >
              Reject
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </PermissionGuard>
  );
};

export default ApprovalManagement;
